import type { DealServiceFormData } from '../types';
import { supabase } from '../supabase';

/**
 * Righe prodotto di un'offerta che superano il "Disponibile" dell'articolo.
 *
 * Il disponibile e' quello annotato a mano sulla scheda articolo
 * (`products.available_quantity`), non la giacenza: chi lo usa (Say Farm)
 * ne tiene fuori anche quote non impegnate. Se e' vuoto l'articolo non e'
 * gestito cosi' e non si avvisa — altrimenti ogni org che non lo compila
 * vedrebbe avvisi su tutto.
 *
 * Le quantita' dello stesso articolo su piu' righe si sommano: due righe da
 * 6 su un disponibile di 10 sono comunque 12.
 */

export interface ProductAvailability {
  name: string;
  available: number | null;
  unit: string | null;
}

export interface OverAvailableLine {
  productId: string;
  name: string;
  requested: number;
  available: number;
  unit: string | null;
}

export async function fetchProductAvailability(
  productIds: string[],
): Promise<Map<string, ProductAvailability>> {
  const ids = [...new Set(productIds.filter(Boolean))];
  const map = new Map<string, ProductAvailability>();
  if (ids.length === 0) return map;
  const { data, error } = await supabase
    .from('products')
    .select('id, name, available_quantity, unit_of_measure_1')
    .in('id', ids);
  if (error) throw error;
  (data ?? []).forEach((p: any) => {
    map.set(p.id, {
      name: p.name,
      available: p.available_quantity ?? null,
      unit: p.unit_of_measure_1 ?? null,
    });
  });
  return map;
}

/** Quantita' richiesta per articolo, sommando tutte le righe prodotto. */
export function requestedByProduct(lines: DealServiceFormData[]): Map<string, number> {
  const totals = new Map<string, number>();
  lines.forEach((line) => {
    if (line.line_type !== 'product' || !line.product_id) return;
    const qty = Number(line.quantity) || 0;
    totals.set(line.product_id, (totals.get(line.product_id) ?? 0) + qty);
  });
  return totals;
}

export function findOverAvailable(
  lines: DealServiceFormData[],
  availability: Map<string, ProductAvailability>,
): OverAvailableLine[] {
  const over: OverAvailableLine[] = [];
  requestedByProduct(lines).forEach((requested, productId) => {
    const info = availability.get(productId);
    if (!info || info.available == null) return;
    if (requested > info.available) {
      over.push({ productId, name: info.name, requested, available: info.available, unit: info.unit });
    }
  });
  return over;
}
